import { Box, Button, Modal, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { SPACE_SECTION, SURFACE_MUTED } from "./layoutTokens";
import { useUserInfoArea } from "./userInfoAreaContext";

export interface UnsavedChangesPromptProps {
  isOpen: boolean;
  /** Selects the body's copy register: "your information" only when the subject is the operator. */
  isSubjectTheOperator?: boolean;
  /** Keep editing — closes the prompt and preserves both the edits and the current selection. */
  onCancel: () => void;
  /** Discard — clears the edits and lets the deferred action proceed. */
  onDiscard: () => void;
}

/**
 * The unsaved-changes prompt for FR-023 (changing the selected user) and FR-024 (leaving the page).
 *
 * **It offers two outcomes and the consequential one is not styled as the default.** Keep-editing is
 * the `primary` action on the left; discard is `tertiary` on the right. The selection or navigation
 * proceeds **only** on discard — cancelling leaves both the edits and the current subject exactly as
 * they were.
 *
 * ⚠️ **The two styles were the other way round until review 2026-09-22, which contradicted the
 * sentence above.** Discard carried `primary` — the heaviest affordance in the dialog, on the
 * irreversible action, in a prompt whose whole purpose is to stop an accidental loss of typing.
 * Non-destructive-by-default is the same reasoning that already routes Escape and the backdrop to
 * keep-editing; do not "restore" visual weight to discard, and note that `UnsavedChangesPrompt.spec`
 * now pins both styles.
 *
 * **FR-076's modal requirements are met by Interstate's `Modal`, not hand-rolled**: it takes focus
 * on open, confines keyboard navigation while open (`disableEnforceFocus` is left at its default so
 * the trap stays on), is dismissible by keyboard, and returns focus to the control that opened it.
 * Those are the four properties T083 asserts, and getting them from the design system is the point —
 * a bespoke overlay would have to re-earn all four.
 *
 * **Mounted only while open**, like `ResetPasswordModal`: `isOpen ? … : null` rather than
 * `show={isOpen}`. There is no password here, so the stakes are lower than in that dialog, but the
 * same reasoning applies to focus — a `Modal` kept mounted with `show={false}` leaves its fiber
 * alive, and reopening it should be a fresh mount so focus is taken again.
 *
 * **`size="small"` means Interstate renders no close button of its own** (`displayCloseButton` is
 * `size === "large"`), and that is deliberate here rather than an omission to fix: unlike the
 * reset-password dialog, this prompt's two actions *are* the full set of outcomes. An `×` would be a
 * third, ambiguous one — neither keeping nor discarding — and FR-023 requires the selection to change
 * only on an explicit choice. Escape still dismisses, which `onHide` routes to keep-editing: the
 * non-destructive branch, so an accidental Escape cannot lose typing.
 */
export const UnsavedChangesPrompt = ({
  isOpen,
  isSubjectTheOperator = false,
  onCancel,
  onDiscard
}: UnsavedChangesPromptProps) => {
  const area = useUserInfoArea();

  return isOpen ? (
    <Modal
      show
      data-testid={"unsaved-changes-prompt"}
      footer={{
        footerComponent: (
          <Box
            sx={{
              borderTop: `1px solid ${SURFACE_MUTED}`,
              columnGap: "8px",
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 24px",
              width: "100%"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: `${area.slug}-unsaved-keep-editing`,
                eventProperties: undefined
              }}
              buttonStyle={"primary"}
              data-testid={"unsaved-keep-editing"}
              onClick={onCancel}
            >
              {Locale.getText("KEEP_EDITING") as string}
            </Button>

            <Button
              analyticsConfiguration={{
                eventName: `${area.slug}-unsaved-discard`,
                eventProperties: undefined
              }}
              buttonStyle={"tertiary"}
              data-testid={"unsaved-discard"}
              onClick={onDiscard}
            >
              {Locale.getText("DISCARD_CHANGES") as string}
            </Button>
          </Box>
        )
      }}
      header={
        /*
         * ⚠️ **All three strings in this prompt use keys that are ALREADY REGISTERED in Label
         * Services, which is why none of them is named `UM_*`.** Do not "tidy" them into
         * area-private keys — that would mean requesting three translations that already exist.
         *
         * | rendered here | key | why |
         * |---|---|---|
         * | title | `FEEMANAGER_UNSAVED_TITLE` | only registered key whose text is byte-identical |
         * | keep | `KEEP_EDITING` | registered as "Keep **E**diting" — capital accepted (below) |
         * | discard | `DISCARD_CHANGES` | registered as "Discard **C**hanges" — same |
         *
         * Chosen by comparing this page's new copy **by value** against the live Label Services
         * `Core` bundle — 25,007 `en_US` labels — and keeping only candidates also populated in
         * `en_CA` **and** `fr_CA`, since a key translated in one locale solves nothing. The two
         * capitalisation changes were accepted by the reviewer on 2026-09-15 in exchange for
         * dropping two registration requests.
         *
         * For `DISCARD_CHANGES` specifically: three other registered keys carry the same English
         * text and all three are worse. `CANCEL_CONFIRM_DISCARD`'s `fr_CA` is untranslated (it
         * literally reads "Discard Changes"), and the two `CMSC_*` ones say "Annuler" — *cancel*,
         * a different action from discarding. Do not switch to any of them.
         *
         * Only the body (`UM_UNSAVED_BODY`, or `MI_UNSAVED_BODY` for the operator) has its own keys:
         * nothing registered matches its text.
         */
        <Typography tag={"h2"} variant={"h5"}>
          {Locale.getText("FEEMANAGER_UNSAVED_TITLE") as string}
        </Typography>
      }
      size={"small"}
      /*
       * Escape and backdrop both route to **keep editing**, the non-destructive branch. Wiring them
       * to discard would make an accidental Escape lose the operator's typing, which is the whole
       * thing SC-007 ("zero silent data loss") is counting.
       */
      onHide={onCancel}
    >
      <Box sx={{ padding: SPACE_SECTION }}>
        <Typography tag={"p"} variant={"body-md"}>
          {
            Locale.getText(
              isSubjectTheOperator ? "MI_UNSAVED_BODY" : "UM_UNSAVED_BODY"
            ) as string
          }
        </Typography>
      </Box>
    </Modal>
  ) : null;
};
